/**
 * CustomerAuthCallback.jsx
 * 
 * Handles Google OAuth callback for customer authentication.
 * Processes session_id from URL fragment and creates session.
 */

import React, { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { Loader2 } from 'lucide-react';

const CustomerAuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { processAuthCallback } = useCustomerAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      // Get session_id from URL fragment
      const hash = location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (!sessionIdMatch) {
        console.error('No session_id in URL');
        navigate('/?signin=true');
        return;
      }

      const sessionId = sessionIdMatch[1];
      
      try {
        const result = await processAuthCallback(sessionId);
        
        if (result.success) {
          // Check for stored redirect path (from review page or other auth-walled pages)
          const reviewRedirect = sessionStorage.getItem('reviewRedirect');
          const authRedirect = sessionStorage.getItem('customerAuthRedirect');
          
          // Clear stored redirects
          sessionStorage.removeItem('reviewRedirect');
          sessionStorage.removeItem('customerAuthRedirect');
          
          // Redirect to stored path or default to my-trips
          const redirectPath = reviewRedirect || authRedirect || '/my-trips';
          navigate(redirectPath, { state: { user: result.customer } });
        } else {
          console.error('Auth failed:', result.message);
          navigate('/?signin=true&error=auth_failed');
        }
      } catch (error) {
        console.error('Auth callback error:', error);
        navigate('/?signin=true&error=auth_failed');
      }
    };

    processSession();
  }, [location, navigate, processAuthCallback]);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default CustomerAuthCallback;
